import { ArticleJsonLd, NextSeo } from 'next-seo'
import { useRouter } from 'next/router'

import { getBaseUrl } from '@/lib/contentful/utils'

import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import { useUrl } from '@/utils/url/useUrl'

import { useEntry } from '../BlogPost.hooks'

type Props = {
	children: React.ReactNode
}

export function BlogPostMeta({ children }: Props) {
	const entry = useEntry()

	const { query, locale = 'en-US' } = useRouter()
	const url = useUrl({ locale })

	const baseUrl = `${url?.protocol}//${url?.host}`

	if ('error' in entry) return <>{children}</>

	const { seoMetadata } = entry.data
	const includeTitleTemplate = Boolean(seoMetadata?.includeTitleTemplate)
	const title = includeTitleTemplate ? `${seoMetadata?.title} | BigCommerce` : seoMetadata?.title

	return (
		<>
			<ArticleJsonLd
				type="Article"
				publisherName="BigCommerce"
				publisherLogo={
					'https://www-cdn.bigcommerce.com/assets/BigCommerce-Social-Image-Generic-Facebook.jpg'
				}
				title={title ?? ''}
				description={seoMetadata?.description ?? ''}
				images={[seoMetadata?.image?.url as string]}
				datePublished={new Date(entry?.data?.publishedDate ?? new Date())?.toISOString()}
				dateModified={new Date(entry?.data?.sys?.publishedAt ?? new Date())?.toISOString()}
				url={seoMetadata?.canonicalUrl || `${baseUrl}/blog/${entry.data.slug}`}
				authorName={[
					{
						name: entry?.data?.author?.fullName ?? '',
						url: `${baseUrl}/blog/author/${entry?.data?.author?.slug}`
					}
				]}
			/>

			<NextSeo
				title={title ?? undefined}
				description={seoMetadata?.description ?? undefined}
				canonical={seoMetadata?.canonicalUrl || `${baseUrl}/blog/${query.slug}/`}
				languageAlternates={[
					{ hrefLang: 'en-au', href: `${getBaseUrl('en-AU')}/blog/${query.slug}/` },
					{ hrefLang: 'en-gb', href: `${getBaseUrl('en-GB')}/blog/${query.slug}/` },
					{ hrefLang: 'en-us', href: `${getBaseUrl('en-US')}/blog/${query.slug}/` },
					{ hrefLang: 'x-default', href: `${getBaseUrl()}/blog/${query.slug}/` }
				]}
				openGraph={{
					title: title ?? undefined,
					description: seoMetadata?.description ?? '',
					url: `${baseUrl}/blog/${query.slug}/`,
					images: seoMetadata?.image?.url
						? [
								{
									url: seoMetadata?.image?.url,
									width: seoMetadata?.image?.width,
									height: seoMetadata?.image?.height,
									alt: 'Bigcommerce logo',
									type: 'image/jpeg'
								}
						  ]
						: undefined,
					locale: getSeoLocale(locale),
					siteName: 'The BigCommerce Blog',
					article: {
						publishedTime: entry.data.publishedDate
							? new Date(entry.data.publishedDate).toISOString()
							: new Date()?.toISOString(),
						modifiedTime: entry.data.sys.publishedAt
							? new Date(entry.data.sys.publishedAt).toISOString()
							: new Date()?.toISOString(),
						authors: [`${baseUrl}/blog/author/${entry?.data?.author?.slug}`],
						tags: entry?.data?.tagsCollection?.items?.map((tag) => tag?.name ?? '')?.filter(Boolean)
					}
				}}
				nofollow={Boolean(seoMetadata?.nofollow)}
				noindex={Boolean(seoMetadata?.noindex)}
			/>

			{children}
		</>
	)
}
